import {
  WSAlert,
  WSAlertAction,
  WSAlertProps,
  WSElement,
  WSFlexBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { IWorkLog, WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";

type AlertConfig = Pick<WSAlertProps, "theme" | "icon" | "title"> & {
  message: React.ReactNode;
  action?: WSAlertAction;
};

type Props = {
  workLog: IWorkLog | undefined;
  onCancelEditingRequest?: () => void;
  onViewHistory?: () => void;
  clientName?: string;
};

const InlineComment: React.FC<{
  label: string;
  comment: string;
  date?: Date | string;
}> = ({ label, comment, date }) => (
  <WSElement mt="M" colorBackground="white" p="M" style={{ borderRadius: 2 }}>
    <WSFlexBox.CenterY wrap="wrap" gap="S">
      <WSText.ParagraphXs color="gray500">{label}</WSText.ParagraphXs>
      {date && (
        <>
          <WSElement
            colorBackground="gray400"
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0
            }}
          />
          <WSText.ParagraphXs color="gray500">
            {toWSDateString(date, "monthDayYear")}
          </WSText.ParagraphXs>
        </>
      )}
    </WSFlexBox.CenterY>
    <WSText.ParagraphXs color="gray600" py="S">
      &ldquo;{comment}&rdquo;
    </WSText.ParagraphXs>
  </WSElement>
);

export const AlertWorkLogReviewStatusPayee: React.FC<Props> = ({
  workLog,
  onCancelEditingRequest,
  onViewHistory,
  clientName
}) => {
  const history = useHistory();

  const payerComment = (workLog as any)?.payerOwnedData?.comment as
    | string
    | undefined;
  const payeeComment = (workLog as any)?.payeeOwnedData?.comment as
    | string
    | undefined;

  // Only one comment exists at a time
  const comment = payerComment || payeeComment;
  const commentLabel = payerComment
    ? `Comment from ${clientName || "client"}`
    : "Your comment";

  const alertConfigs = useMemo((): AlertConfig[] => {
    const configs: AlertConfig[] = [];

    switch (workLog?.status) {
      case WorkLogStatus.Draft:
        break;
      case WorkLogStatus.Approved:
        configs.push({
          theme: "success",
          icon: "check-circle",
          title: "Work log approved",
          message: (
            <>
              Work log was approved and an invoice was generated.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={workLog?.updatedAt}
                />
              )}
            </>
          ),
          action: workLog.invoiceId
            ? {
                text: "View invoice",
                onClick: () =>
                  history.push(`/member/invoices/${workLog.invoiceId}`)
              }
            : undefined
        });
        break;
      case "InReview" as WorkLogStatus:
        if (payeeComment) {
          configs.push({
            theme: "neutral",
            icon: "time",
            title: "Awaiting client response",
            message: (
              <>
                You have requested that the submitted work log be returned for
                editing. The client can respond to the request by returning the
                work log back to you for corrections, make edits themselves, or
                approve it as-is. You&apos;ll be notified of their decision.
                <InlineComment
                  label={commentLabel}
                  comment={payeeComment}
                  date={workLog?.updatedAt}
                />
              </>
            ),
            action: onCancelEditingRequest
              ? {
                  text: "Cancel request for editing",
                  onClick: onCancelEditingRequest
                }
              : undefined
          });
        } else {
          configs.push({
            theme: "neutral",
            icon: "info-circle",
            title: "Awaiting client approval",
            message: (
              <>
                Your client will review and either approve this work log or
                request changes. You&apos;ll be notified of their decision. Work
                logs cannot be edited while in review. Need to make corrections?
                Use{" "}
                <WSText.ParagraphSm inline weight="medium">
                  &quot;Request editing&quot;
                </WSText.ParagraphSm>{" "}
                above to ask the client to send the work log back to you for
                editing.
              </>
            )
          });
        }
        break;
      case "InProgress" as WorkLogStatus:
        configs.push({
          theme: "warning",
          icon: "alert",
          title: "Action required",
          message: (
            <>
              The work log was returned to you for editing. Once the work log is
              ready, resubmit it for client approval.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={workLog?.updatedAt}
                />
              )}
            </>
          )
        });
        break;
      case "Rejected" as WorkLogStatus:
        configs.push({
          theme: "error",
          icon: "block",
          title: "Rejected",
          message: (
            <>
              The work log was rejected and was not invoiced.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={workLog?.updatedAt}
                />
              )}
            </>
          ),
          action: onViewHistory
            ? {
                text: "View history",
                onClick: onViewHistory
              }
            : undefined
        });
        break;
    }

    return configs;
  }, [
    workLog,
    history,
    comment,
    commentLabel,
    payeeComment,
    onCancelEditingRequest,
    onViewHistory
  ]);

  if (alertConfigs.length === 0) {
    return null;
  }

  return (
    <WSList gap="M">
      {alertConfigs.map(({ message, ...alertProps }, index) => (
        <WSAlert key={index} {...alertProps}>
          {message}
        </WSAlert>
      ))}
    </WSList>
  );
};
